import {
  OreButton,
  OreHeading,
  OreInput,
  OreModal,
  OreModalBox,
  OreModalBoxBody,
  OreModalBoxFooter,
  OreStack,
  OreText,
  OreTooltip,
  SvgInfo
} from '@edenredespana/oreneta'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { handleErrors } from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useTpv } from '@/Store/ui/context'
import { TpvForm } from '@/Store/ui/hooks'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import Bill from '@/Shared/ui/assets/tpv-bill.png'

export type AddTpvModalActions = {
  open: () => void
}

export const AddTpvModal = forwardRef<AddTpvModalActions>((_, ref): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  const { addNewTpv, form } = useTpv()

  const { register, formState } = form
  const { errors } = handleErrors(formState)

  useImperativeHandle(ref, () => ({
    open: async (): Promise<void> => {
      form.reset()
      setOpen(true)
    },
  }))

  const saveAndClose = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event?.preventDefault()

    form.handleSubmit(async (data: TpvForm) => {
      const result = await addNewTpv(data)
      if (result) {
        setOpen(false)
      }
    })()
  }

  return (
    <OreModal open={open} handleOnClose={() => setOpen(false)}>
      <OreModalBox size="medium" handleOnClose={() => setOpen(false)}>
        <OreModalBoxBody>
          <OreHeading align="left" size="headline-md">
            {t(storesTranslation.tpvs.modal.title)}
          </OreHeading>
          <OreStack space="large">
            <OreText align="left">{t(storesTranslation.tpvs.modal.text)}</OreText>
            <OreStack direction="column" space="xsmall">
              <OreStack direction="row" space="xsmall">
                <OreText align="left">{t(storesTranslation.tpvs.modal.terminalId.label)}</OreText>
                <OreTooltip
                  placement="top"
                  text={
                    <img
                      src={Bill}
                      alt="ticket"
                      style={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                  }
                >
                  <span style={{ display: 'inline-flex' }}>
                    <SvgInfo />
                  </span>
                </OreTooltip>
              </OreStack>
              <OreInput
                {...register('terminalMid')}
                {...errors('terminalMid')}
                id="name"
                legend={t(storesTranslation.tpvs.modal.legend)}
                placeholder={t(AutoEnrollmentTranslation.storeTpvsData.tpvs.placeHolder)}
                required
                maxLength={255}
              />
            </OreStack>
          </OreStack>
        </OreModalBoxBody>
        <OreModalBoxFooter>
          <OreButton onClick={() => setOpen(false)} size="small" category="secondary">
            {t(formTranslation.common.buttons.cancel)}
          </OreButton>
          <OreButton
            data-testid="confirm"
            onClick={saveAndClose}
            size="small"
            category="primary"
            disabled={!formState.isValid}
          >
            {t(formTranslation.common.buttons.add)}
          </OreButton>
        </OreModalBoxFooter>
      </OreModalBox>
    </OreModal>
  )
})
